import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, PencilLine, X } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { MaterialsChecklist, type MaterialLine } from './MaterialsChecklist';

/**
 * Correcting what was recorded as used on site.
 *
 * Only rendered for `material:record` — the technician who did the work, or an
 * admin. A dispatcher sees the table above this and no way to touch it: they
 * were not at the site, so anything they entered would be a guess or a
 * transcription, and a transcription nobody is accountable for is how stock
 * disappears without a trail.
 *
 * Hidden behind a button rather than always open. This is a correction, not
 * data entry — the common case is that the technician's list is right, and a
 * form standing open invites edits that were never needed.
 */
export function CorrectMaterials({
  ticketId,
  initial,
  initialOther,
}: {
  ticketId: string;
  initial: MaterialLine[];
  initialOther: string;
}) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState<MaterialLine[]>(initial);
  const [other, setOther] = useState(initialOther);
  const [error, setError] = useState<string | null>(null);

  // The API refuses this independently; hiding it is a courtesy, not the rule.
  const allowed = can('material:record');

  const catalogue = useQuery({
    queryKey: ['material-catalogue'],
    queryFn: api.materialCatalogue,
    // Fetched only once somebody opens the form — a dispatcher never asks for
    // a list they are not allowed to submit against.
    enabled: allowed && open,
    staleTime: 60 * 60 * 1000,
  });

  const save = useMutation({
    mutationFn: () => api.setTicketMaterials(ticketId, lines, other),
    onSuccess: async () => {
      setOpen(false);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
    },
    onError: (caught: Error) => setError(caught.message),
  });

  if (!allowed) return null;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="btn-secondary mt-3 text-sm"
      >
        <PencilLine className="h-4 w-4" aria-hidden="true" />
        Correct what was used
      </button>
    );
  }

  return (
    <div className="mt-3 rounded-2xl border border-primary-200 bg-primary-50/40 p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h4 className="font-heading text-base">Correct what was used</h4>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-lg p-1.5 hover:bg-muted"
          aria-label="Close"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <p className="mb-3 text-sm text-muted-foreground">
        What was recorded is replaced by what you leave here, and the previous list is kept in the
        audit log. Correct a mistyped serial or a wrong unit — do not use this to record a job you
        did not attend.
      </p>

      {error && (
        <p role="alert" className="mb-3 rounded-xl bg-danger-50 p-3 text-sm text-danger-700">
          {error}
        </p>
      )}

      {catalogue.isPending ? (
        <Loader2 className="mx-auto h-5 w-5 animate-spin text-primary-500" aria-label="Loading" />
      ) : (
        <MaterialsChecklist
          catalogue={catalogue.data?.catalogue ?? []}
          categoryLabels={catalogue.data?.categoryLabels ?? {}}
          lines={lines}
          other={other}
          onChange={setLines}
          onOtherChange={setOther}
        />
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          className="btn-primary"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          Save the correction
        </button>
        <button type="button" className="btn-secondary" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}
